import "./ClientAccountDetailsFormComponent.scss";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {Misc} from "../../../../constants";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import LoaderComponent from "../../../../shared/components/loader/LoaderComponent";
import CardComponent from "../../../../shared/components/card/CardComponent";
import StatusCardComponent from "../../../../shared/components/status-card/StatusCardComponent";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import {getClientCommunicationReferralDetails} from "../../../../store/actions/client.action";
import DataLabelValueComponent from "../../../../shared/components/data-label-value/DataLabelValueComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import HeadingWithNavigationComponent
    from "../../../../shared/components/heading-with-navigation/HeadingWithNavigationComponent";
import * as Yup from "yup";
import {IClientCommunicationReferralDetails} from "../../../../shared/models/client.model";
import DetailsViewCardComponent from "../../../../shared/components/details-view-card/DetailsViewCardComponent";

interface ClientAccountDetailsFormComponentProps {
    clientCommunicationReferralDetails: boolean | undefined;
    isClientCommunicationReferralDetailsLoaded: boolean | undefined;
    isClientCommunicationReferralDetailsLoading: boolean | undefined;
    isClientCommunicationReferralDetailsLoadingFailed: boolean | undefined;
    onCancel: () => void;
    clientId: string;
    onSave: (clientAccountDetailsFormDetails: any) => void;
}

const ClientCommunicationReferralDetailsFormValidationSchema = Yup.object({
    communication_preferences: Yup.object({
        appointment_reminders: Yup.string()
            .required('Appointment Reminders is required'),
        appointment_confirmations: Yup.string()
            .required('Appointment Confirmations is required'),
    }),
    referral_details: Yup.object({
        source_info_name: Yup.string().when('source', {
            is: (source: any) => (source === 'friends_family_colleague' || source === 'social_media' || source === 'other'),
            then: Yup.string().required('Field is required'),
            otherwise: Yup.string(),
        }),

        source_info_relationship: Yup.string().when('source', {
            is: (source: any) => (source === 'friends_family_colleague'),
            then: Yup.string().required('Field is required'),
            otherwise: Yup.string(),
        })

    })
});

const ClientCommunicationReferralDetailsFormInitialValues: IClientCommunicationReferralDetails = {
    communication_preferences: {
        appointment_reminders: "",
        appointment_confirmations: ""
    },
    referral_details: {
        source: "",
        source_platform: "",
        source_info_first_name: "",
        source_info_last_name: "",
        source_info_phone: "",
        source_info_email: "",
        source_info_relationship: "",
        comment: ''
    }
}


const ClientAccountDetailsFormComponent = (props: ClientAccountDetailsFormComponentProps) => {

    const {
        onCancel,
        onSave,
        clientId,
        clientCommunicationReferralDetails,
        isClientCommunicationReferralDetailsLoaded,
        isClientCommunicationReferralDetailsLoading,
        isClientCommunicationReferralDetailsLoadingFailed
    } = props;
    const [clientAccountDetailsFormInitialValues, setClientAccountDetailsFormInitialValues] = useState<any>(_.cloneDeep(ClientCommunicationReferralDetailsFormInitialValues));
    const [isClientAccountDetailsFormSavingInProgress, setIsClientAccountDetailsFormSavingInProgress] = useState(false);
    const dispatch = useDispatch();

    const {
        communicationModeTypeList,
        // referralTypeList,
        // relationshipList,
        // socialMediaPlatformList
    } = useSelector((state: IRootReducerState) => state.staticData);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
        setSubmitting(true);
        setIsClientAccountDetailsFormSavingInProgress(true)
        const payload = {...values, is_communication_details_updated: true, client_id: clientId};
        CommonService._client.ClientCommunicationReferralDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setSubmitting(false);
                dispatch(getClientCommunicationReferralDetails());
                onSave(response.data);
                setIsClientAccountDetailsFormSavingInProgress(false)
            })
            .catch((error: any) => {
                setIsClientAccountDetailsFormSavingInProgress(false)
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [dispatch,clientId, onSave]);

    useEffect(() => {
        if (clientCommunicationReferralDetails) {
            setClientAccountDetailsFormInitialValues(clientCommunicationReferralDetails);
        }
    }, [dispatch, clientCommunicationReferralDetails]);

    return (
        <div className={'client-account-details-form-component'}>
            <HeadingWithNavigationComponent onBackNavigation={onCancel}
                                            title={"Back"}/>
            <>
                {
                    isClientCommunicationReferralDetailsLoading && <div>
                        <LoaderComponent/>
                    </div>
                }
                {
                    isClientCommunicationReferralDetailsLoadingFailed &&
                    <StatusCardComponent title={"Failed to fetch account Details"}/>
                }
            </>
            {
                isClientCommunicationReferralDetailsLoaded && clientCommunicationReferralDetails && <>

                    <Formik
                        validationSchema={ClientCommunicationReferralDetailsFormValidationSchema}
                        initialValues={clientAccountDetailsFormInitialValues}
                        onSubmit={onSubmit}
                        validateOnChange={false}
                        validateOnBlur={true}
                        enableReinitialize={true}
                        validateOnMount={true}>
                        {({values, errors, isValid, setFieldValue, validateForm}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [validateForm, values]);
                            return (<CardComponent>
                                    <div className={'edit-communication-preferences-heading'}>
                                        Edit Communication Preferences
                                    </div>
                                    <Form noValidate={true} className={"t-form"}>
                                        <div className={'communication-wrapper'}>
                                            <DetailsViewCardComponent title={"Communication and Referral Details"}
                                            >
                                                <div className="t-form-controls">
                                                    <div className="ts-row">
                                                        <div className="ts-col-6">
                                                            <DataLabelValueComponent label={"Appointment Confirmations*"}>
                                                                How would you like to receive appointment confirmations
                                                            </DataLabelValueComponent>
                                                        </div>
                                                        <div className={'ts-col-3'}/>
                                                        <div className="ts-col-3">
                                                            <Field
                                                                name={`communication_preferences.appointment_confirmations`}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikSelectDropdownComponent
                                                                            options={communicationModeTypeList}
                                                                            placeholder={"Select Option"}
                                                                            formikField={field}
                                                                            fullWidth={true}
                                                                            isClearable={false}
                                                                            required={true}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </div>
                                                    </div>

                                                    <div className="ts-row">
                                                        <div className="ts-col-6">
                                                            <DataLabelValueComponent label={"Appointment Reminders*"}>
                                                                How would you like to receive appointment reminders
                                                            </DataLabelValueComponent>
                                                        </div>
                                                        <div className={'ts-col-3'}/>
                                                        <div className="ts-col-3">
                                                            <Field name={`communication_preferences.appointment_reminders`}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikSelectDropdownComponent
                                                                            options={communicationModeTypeList}
                                                                            placeholder={"Select Option"}
                                                                            formikField={field}
                                                                            fullWidth={true}
                                                                            required={true}
                                                                            isClearable={false}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </div>
                                                        <div className="ts-col-2"/>
                                                    </div>
                                                    {/*    <HorizontalLineV2Component/>*/}
                                                    {/*    <FormControlLabelComponent label={"Referral Details"}/>*/}
                                                    {/*    <div className="ts-row">*/}
                                                    {/*        <div className="ts-col-7">*/}
                                                    {/*            <DataLabelValueComponent label={"How did you find us?"}>*/}
                                                    {/*                Please choose an option that best describes how you heard*/}
                                                    {/*                about*/}
                                                    {/*                us.*/}
                                                    {/*            </DataLabelValueComponent>*/}
                                                    {/*        </div>*/}
                                                    {/*        <div className="ts-col-2"/>*/}
                                                    {/*        <div className="ts-col-3">*/}
                                                    {/*            <Field name={`referral_details.source`}>*/}
                                                    {/*                {*/}
                                                    {/*                    (field: FieldProps) => (*/}
                                                    {/*                        <FormikSelectDropdownComponent*/}
                                                    {/*                            options={referralTypeList}*/}
                                                    {/*                            placeholder={"Select Option"}*/}
                                                    {/*                            formikField={field}*/}
                                                    {/*                            fullWidth={true}*/}
                                                    {/*                            isClearable={false}*/}
                                                    {/*                            onUpdate={() => {*/}
                                                    {/*                                setFieldValue('referral_details.source_info_name', '');*/}
                                                    {/*                                setFieldValue('referral_details.source_info_phone', '');*/}
                                                    {/*                                setFieldValue('referral_details.source_info_email', '');*/}
                                                    {/*                                setFieldValue('referral_details.source_info_relationship', '');*/}
                                                    {/*                            }}*/}
                                                    {/*                        />*/}
                                                    {/*                    )*/}
                                                    {/*                }*/}
                                                    {/*            </Field>*/}
                                                    {/*        </div>*/}
                                                    {/*        /!*<div className="ts-col-1"/>*!/*/}
                                                    {/*    </div>*/}
                                                    {/*    {*/}
                                                    {/*        values.referral_details.source === "friends_family_colleague" && <>*/}
                                                    {/*            <DataLabelValueComponent label={"Please complete the following:"}>*/}
                                                    {/*            </DataLabelValueComponent>*/}
                                                    {/*            <div className="ts-row">*/}
                                                    {/*                <div className="ts-col">*/}
                                                    {/*                    <Field name={`referral_details.source_info_name`}>*/}
                                                    {/*                        {*/}
                                                    {/*                            (field: FieldProps) => (*/}
                                                    {/*                                <FormikInputComponent*/}
                                                    {/*                                    label={"Full Name"}*/}
                                                    {/*                                    formikField={field}*/}
                                                    {/*                                    fullWidth={true}*/}
                                                    {/*                                    required={true}*/}
                                                    {/*                                />*/}
                                                    {/*                            )*/}
                                                    {/*                        }*/}
                                                    {/*                    </Field>*/}
                                                    {/*                </div>*/}
                                                    {/*                <div className="ts-col">*/}
                                                    {/*                    <Field name={`referral_details.source_info_phone`}>*/}
                                                    {/*                        {*/}
                                                    {/*                            (field: FieldProps) => (*/}
                                                    {/*                                <FormikPhoneInputComponent*/}
                                                    {/*                                    label={"Phone Number"}*/}
                                                    {/*                                    formikField={field}*/}
                                                    {/*                                    fullWidth={true}*/}
                                                    {/*                                />*/}
                                                    {/*                            )*/}
                                                    {/*                        }*/}
                                                    {/*                    </Field>*/}
                                                    {/*                </div>*/}
                                                    {/*                <div className="ts-col-2"/>*/}
                                                    {/*            </div>*/}
                                                    {/*            <div className="ts-row">*/}
                                                    {/*                <div className="ts-col">*/}
                                                    {/*                    <Field name={`referral_details.source_info_email`}>*/}
                                                    {/*                        {*/}
                                                    {/*                            (field: FieldProps) => (*/}
                                                    {/*                                <FormikInputComponent*/}
                                                    {/*                                    label={"Email"}*/}
                                                    {/*                                    type={"email"}*/}
                                                    {/*                                    placeholder={"Enter Email Address"}*/}
                                                    {/*                                    formikField={field}*/}
                                                    {/*                                    fullWidth={true}*/}
                                                    {/*                                />*/}
                                                    {/*                            )*/}
                                                    {/*                        }*/}
                                                    {/*                    </Field>*/}
                                                    {/*                </div>*/}
                                                    {/*                <div className="ts-col">*/}
                                                    {/*                    <Field name={`referral_details.source_info_relationship`}>*/}
                                                    {/*                        {*/}
                                                    {/*                            (field: FieldProps) => (*/}
                                                    {/*                                <FormikSelectDropdownComponent*/}
                                                    {/*                                    options={relationshipList}*/}
                                                    {/*                                    label={"Relationship"}*/}
                                                    {/*                                    isClearable={false}*/}
                                                    {/*                                    placeholder={"Select Option"}*/}
                                                    {/*                                    formikField={field}*/}
                                                    {/*                                    fullWidth={true}*/}
                                                    {/*                                    required={true}*/}
                                                    {/*                                />*/}
                                                    {/*                            )*/}
                                                    {/*                        }*/}
                                                    {/*                    </Field>*/}
                                                    {/*                </div>*/}
                                                    {/*                <div className="ts-col-2"/>*/}
                                                    {/*            </div>*/}
                                                    {/*        </>*/}
                                                    {/*    }*/}
                                                    {/*    {*/}
                                                    {/*        values.referral_details.source === "social_media" && <>*/}
                                                    {/*            <DataLabelValueComponent label={"Please select an option:*"}>*/}
                                                    {/*            </DataLabelValueComponent>*/}
                                                    {/*            <div className="ts-row">*/}
                                                    {/*                <div className="ts-col-5">*/}
                                                    {/*                    <Field name={`referral_details.source_info_name`}>*/}
                                                    {/*                        {*/}
                                                    {/*                            (field: FieldProps) => (*/}
                                                    {/*                                <FormikSelectDropdownComponent*/}
                                                    {/*                                    options={socialMediaPlatformList}*/}
                                                    {/*                                    placeholder={"Select Option"}*/}
                                                    {/*                                    required={true}*/}
                                                    {/*                                    formikField={field}*/}
                                                    {/*                                    isClearable={false}*/}
                                                    {/*                                    fullWidth={true}*/}
                                                    {/*                                />*/}
                                                    {/*                            )*/}
                                                    {/*                        }*/}
                                                    {/*                    </Field>*/}
                                                    {/*                </div>*/}
                                                    {/*            </div>*/}
                                                    {/*        </>*/}
                                                    {/*    }*/}
                                                    {/*    {*/}
                                                    {/*        values.referral_details.source === "other" && <>*/}
                                                    {/*            <DataLabelValueComponent label={"Please explain"}>*/}
                                                    {/*            </DataLabelValueComponent>*/}
                                                    {/*            <div className="ts-row">*/}
                                                    {/*                <div className="ts-col-md-10">*/}
                                                    {/*                    <Field name={`referral_details.source_info_name`}>*/}
                                                    {/*                        {*/}
                                                    {/*                            (field: FieldProps) => (*/}
                                                    {/*                                <FormikTextAreaComponent*/}
                                                    {/*                                    label={"Other info"}*/}
                                                    {/*                                    formikField={field}*/}
                                                    {/*                                    fullWidth={true}*/}
                                                    {/*                                    required={true}*/}
                                                    {/*                                    rows={3}*/}
                                                    {/*                                />*/}
                                                    {/*                            )*/}
                                                    {/*                        }*/}
                                                    {/*                    </Field>*/}
                                                    {/*                </div>*/}
                                                    {/*            </div>*/}
                                                    {/*        </>*/}
                                                    {/*    }*/}
                                                </div>
                                            </DetailsViewCardComponent>
                                        </div>
                                        <div className="t-form-actions">
                                            <ButtonComponent
                                                id={"home_btn"}
                                                variant={"outlined"}
                                                onClick={onCancel}
                                                className={'mrg-right-15'}
                                                disabled={isClientAccountDetailsFormSavingInProgress}
                                                color={"accent"}
                                            >
                                                Cancel
                                            </ButtonComponent>&nbsp;
                                            <ButtonComponent
                                                id={"save_next_btn"}
                                                isLoading={isClientAccountDetailsFormSavingInProgress}
                                                disabled={isClientAccountDetailsFormSavingInProgress || !isValid}
                                                type={"submit"}
                                                color={"accent"}
                                            >
                                                {isClientAccountDetailsFormSavingInProgress ? "Saving" : "Save"}
                                            </ButtonComponent>
                                        </div>
                                    </Form>
                                </CardComponent>
                            )
                        }}
                    </Formik>
                </>
            }
        </div>
    );

};

export default ClientAccountDetailsFormComponent;
